import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'
import { clearChunkReloadFlag, CHUNK_RELOAD_SESSION_KEY } from '@/lib/lazyWithRetry'
import { initGoogleAnalytics, trackPageview } from '@/lib/analytics'
import { createPageviewScheduler } from '@/lib/analytics/pageviewScheduler'
import { initClarity, setClarityTags } from '@/lib/analytics/clarity'
import { clarityRouteTags } from '@/lib/analytics/clarityTags'
import { initContactLinkTracking } from '@/lib/analytics/linkClickEvents'
import { router } from '@/app/router'
import '@/styles/globals.css'
import 'react-toastify/dist/ReactToastify.css'

clearChunkReloadFlag()

initGoogleAnalytics()
initClarity()
initContactLinkTracking()

const pageviews = createPageviewScheduler({ send: (path) => trackPageview(path), settleMs: 300 })
const locationId = (l: { key: string; pathname: string; search: string }) => `${l.key}|${l.pathname}${l.search}`
let lastLocationId = locationId(router.state.location)
pageviews.navigate('POP', router.state.location.pathname + router.state.location.search)
setClarityTags(clarityRouteTags(router.state.location.pathname, router.state.location.search))
router.subscribe(({ navigation, location, historyAction }) => {
  if (navigation.state !== 'idle' || locationId(location) === lastLocationId) return
  lastLocationId = locationId(location)
  pageviews.navigate(historyAction, location.pathname + location.search)
  setClarityTags(clarityRouteTags(location.pathname, location.search))
})
window.addEventListener('pagehide', () => pageviews.flush())

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  try {
    if (!sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1')
      window.location.reload()
    }
  } catch {
    window.location.reload()
  }
})

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')

// Remove server-injected SEO crawl block so React owns the visible UI (no duplicate H1).
document.getElementById('seo-landing-static')?.remove()

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>
)
